import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", description: "", mandatory: true, blocking: true, has_expiry: true, alert_days: [60, 30, 15, 7], applies_positions: [], applies_areas: [], applies_worker_types: [] };
const FLAGS = [["mandatory", "Obligatorio"], ["blocking", "Bloqueante"], ["has_expiry", "Tiene vencimiento"]];

export default function DocTypes() {
  const { can } = useAuth();
  const canEdit = can("rrhh.requisitos.gestionar");
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/rrhh/doc-types").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const body = { ...edit, alert_days: String(edit.alert_days).split(",").map((x) => parseInt(x, 10)).filter((x) => x > 0) };
    try { edit.id ? await api.patch(`/rrhh/doc-types/${edit.id}`, body) : await api.post("/rrhh/doc-types", body); toast.success("Tipo guardado"); setEdit(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (t) => { try { await api.patch(`/rrhh/doc-types/${t.id}`, t, { params: { active: !t.active } }); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="doc-types-page">
      <PageHeader eyebrow="RR.HH." title="Tipos de documentos" subtitle="Catálogo configurable: obligatoriedad, vencimiento, días de alerta y carácter bloqueante para la habilitación."
        actions={canEdit && <Button data-testid="create-doc-type-btn" className="rounded-full" onClick={() => setEdit({ ...EMPTY })}><Plus size={16} className="mr-1" />Nuevo tipo</Button>} />
      <div className="syc-card overflow-hidden">
        {items.length === 0 ? <EmptyState title="Sin tipos de documento" text="Ej.: Cédula de identidad, Examen preocupacional, Licencia de conducir." /> : items.map((t) => (
          <div key={t.id} data-testid={`doc-type-row-${t.id}`} className={`flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] text-sm ${t.active ? "" : "opacity-50"}`}>
            <button className="font-semibold flex-1 min-w-[180px] text-left hover:underline" onClick={() => canEdit && setEdit({ ...t })} data-testid={`doc-type-edit-${t.id}`}>{t.name}</button>
            {t.blocking && <StatusBadge status="bloqueado" label="Bloqueante" />}{t.mandatory && <StatusBadge status="info" label="Obligatorio" />}
            <span className="text-xs text-[#5b6656]">{t.has_expiry ? `Alertas: ${t.alert_days.join("/")} días` : "Sin vencimiento"}</span>
            {canEdit && <Switch data-testid={`doc-type-toggle-${t.id}`} checked={t.active} onCheckedChange={() => toggle(t)} />}
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="doc-type-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Editar tipo" : "Nuevo tipo de documento"}</DialogTitle></DialogHeader>
          {edit && <div className="space-y-4">
            <Field label="Nombre *"><Input data-testid="doc-type-name-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Descripción"><Input data-testid="doc-type-desc-input" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
            <div className="flex flex-wrap gap-5">{FLAGS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm"><Switch data-testid={`doc-type-${k}-switch`} checked={edit[k]} onCheckedChange={(v) => setEdit({ ...edit, [k]: v })} />{l}</label>)}</div>
            {edit.has_expiry && <Field label="Días de alerta (separados por coma)"><Input data-testid="doc-type-alerts-input" value={String(edit.alert_days)} onChange={(e) => setEdit({ ...edit, alert_days: e.target.value })} /></Field>}
          </div>}
          <DialogFooter><Button data-testid="doc-type-save-btn" className="rounded-full" onClick={save}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
